import classNames from "classnames";
import React, { type JSX, useEffect, useRef } from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { ErrorBoundary } from "../../../components/ErrorBoundary";
import { Link } from "../../../components/Link";
import { ModalStyles } from "../../../components/Modal";
import {
    UncontrolledPopover,
    UncontrolledPopoverRef,
} from "../../../components/Popover";
import { SVGIcon } from "../../../components/SVGIcon";
import { WebPageURL } from "../../../models/nominals";
import { BreakPoint } from "../../../models/screen";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { getViewportBreakpoint } from "../../../utils/detectMobile";
import { format } from "../../../utils/format";
import { usePageSetting } from "../../../utils/settings";
import { BasketLines } from "../BasketLines";
import {
    BasketLineVariant,
    PredictedDeliveryDateDisplayType,
    PreferredDeliveryDateDisplayType,
} from "../constants";
import {
    addBasketLine,
    loadConcreteBundles,
    reloadBasketIfOutdated,
    removeBasketLine,
    removeVoucherCode,
    updateBasketLineQuantity,
} from "../thunks";

import styles from "./index.module.scss";

interface BasketMenuIconProps {
    basketURL: WebPageURL;
    checkoutURL: WebPageURL;
    basketLineVariant: BasketLineVariant;
    isPageInsideIFrame: boolean;
    className?: string;
    buildPromoComponents?: (closeModal: () => void) => JSX.Element | null;
    children?: React.ReactNode;
    addPopperArrow?: boolean;
    getPreHeaderHeight?: number | null;
    customIcon?: string;
    showLabel?: boolean;
    isSSR?: boolean;
}

const getProductUpsell = () => {
    return null;
};

const isMobileWidth = (): boolean => {
    const currentBreakpoint = getViewportBreakpoint();
    return currentBreakpoint < BreakPoint.SMALL;
};

// Standardize mini-cart height as viewport height minus header and service footer
// TODO: When we make BasketMenuIcon an SSR component, we can get settings.tsicommon_cms.ServiceFooterSettings.show_footer
// And calculate these heights more accurately
const getModalHeight = (appSlugName: string | null): string => {
    switch (appSlugName) {
        case "tempurpedic":
        case "sale2": {
            return "calc(100vh - 128px - 50px)";
        }
        case "stearns": {
            return "calc(100vh - 45px - 50px)";
        }
        case "pillows": {
            return "calc(100vh - 65px)";
        }
        case "cocoon": {
            return "calc(100vh - 120px)";
        }
    }
    return "";
};

const getCloseButtonTopValue = (appSlugName: string | null): string => {
    if (appSlugName === "cocoon") {
        return isMobileWidth() ? "2.5rem" : "3rem";
    }
    return "2rem";
};

const buildBasketIcon = (
    totalQuantity: number,
    showLabel: boolean | undefined,
    customIcon: string | undefined,
): React.ReactNode => {
    const shouldHideLabel = !showLabel || (showLabel && isMobileWidth());

    const labelClasses = classNames({
        "ada-screenreader-only": shouldHideLabel,
        [styles.label]: !shouldHideLabel,
    });
    return (
        <>
            {/* TODO(i18n): Use t tagged template: {t`View Cart`} */}
            <span className={labelClasses}>View Cart</span>
            <span
                className={`${styles.cartNumber} basket-menu-icon__cart-number`}
            >
                {totalQuantity}
            </span>
            {/* TODO(i18n): Use ngettext for proper plural handling: {ngettext(msgid`${totalQuantity} item in cart.`, `${totalQuantity} items in cart.`, totalQuantity)} */}
            <span className="ada-screenreader-only"> items in cart.</span>
            {customIcon ? (
                <SVG
                    aria-hidden="true"
                    className={styles.icon}
                    title={t`Cart Icon`}
                    src={customIcon}
                />
            ) : (
                <SVGIcon
                    name="cartOpen"
                    alt={t`Cart Icon`}
                    aria-hidden="true"
                    className={styles.icon}
                    width={30}
                    height={24}
                />
            )}
        </>
    );
};

export const BasketMenuIcon = (props: BasketMenuIconProps): React.ReactNode => {
    const popover = useRef<UncontrolledPopoverRef>(null);
    const appSlugName = usePageSetting("app_slug");

    const totalQuantity = useAppSelector((rootState) => {
        let totalQuantity = 0;
        if (rootState.checkout.data.basket) {
            totalQuantity = rootState.checkout.data.basket.lines
                .filter((line) => !line.parent)
                .reduce((memo, line) => {
                    return memo + line.quantity;
                }, 0);
        }
        return totalQuantity;
    });
    const basket = useAppSelector(
        (rootState) => rootState.checkout.data.basket,
    );
    const financingPlans = useAppSelector(
        (rootState) => rootState.checkout.data.financing_plans,
    );

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(reloadBasketIfOutdated());
    }, [dispatch]);

    const onAfterOpen = () => {
        const document = getDocument();
        if (!document) {
            return;
        }

        if (isMobileWidth()) {
            document.body.classList.remove("overflow--initial");
            document.body.classList.add("overflow--y-hidden");

            if (props.addPopperArrow) {
                document.documentElement.style.setProperty(
                    "--basket-popover--height-popover-with-arrow-modifier",
                    props.getPreHeaderHeight
                        ? `${props.getPreHeaderHeight}px`
                        : "0px",
                );
            }
        } else {
            document.body.classList.add("overflow--auto");
        }
    };

    const onAfterClose = () => {
        const document = getDocument();
        if (document) {
            document.body.classList.add("overflow--initial");
        }
    };

    const onRequestClose = () => {
        if (popover.current) {
            popover.current.requestClose();
        }
    };

    // TODO(i18n): Use ngettext for proper plural handling instead of ternary: ngettext(msgid`Item`, `Items`, totalQuantity)
    const qtyLabel = totalQuantity === 1 ? "Item" : "Items";
    const isBasketEmpty = totalQuantity === 0;

    const classes = classNames({
        [styles.button]: true,
        [`${props.className}`]: !!props.className,
        [styles.filled]: !isBasketEmpty,
    });

    const basketPopoverClasses = classNames({
        [styles.content]: props.addPopperArrow,
        [styles.popover]: !props.addPopperArrow,
        "basket-popover": true, // LEGACY CSS CLASS
        "basket-popover--addition": basket?.lines.length
            ? basket.lines.length > 2
            : false,
    });

    const modalStyle: ModalStyles = {
        overlay: {
            position: "absolute",
            backgroundColor: "rgba(255, 255, 255, 0)",
        },
        content: {
            border: "none",
            boxShadow: "none",
            maxHeight: props.isPageInsideIFrame
                ? "500px"
                : !props.isSSR && isMobileWidth()
                  ? ""
                  : getModalHeight(appSlugName),
            height: props.isPageInsideIFrame
                ? "inherit"
                : !props.isSSR && isMobileWidth()
                  ? "100%"
                  : getModalHeight(appSlugName),
        },
    };

    const basketSubtotal = basket ? basket.total_excl_tax : "0.00";

    return (
        <UncontrolledPopover
            ref={popover}
            triggerContent={buildBasketIcon(
                totalQuantity,
                props.showLabel,
                props.customIcon,
            )}
            triggerBtnProps={{
                className: classes,
            }}
            popperOptions={{
                placement: "bottom",
                modifiers: props.addPopperArrow
                    ? [
                          {
                              name: "offset",
                              options: {
                                  offset: [-100, 0],
                              },
                          },
                      ]
                    : [],
            }}
            modalProps={{
                contentLabel: t`Your Cart`,
                className: props.addPopperArrow ? styles.popoverWithArrow : "",
                style: props.addPopperArrow ? {} : modalStyle,
                portalClassName: props.addPopperArrow
                    ? styles.modal
                    : "BasketModal",
                bodyOpenClassName: "basket-modal-portal-body-open",
                closeBtnProps: {
                    style: {
                        top: getCloseButtonTopValue(appSlugName),
                        right: "2rem",
                    },
                },
            }}
            showArrow={props.addPopperArrow}
            arrowCSSClass={styles.popperArrow}
            onAfterOpen={onAfterOpen}
            onAfterClose={onAfterClose}
        >
            <div className={basketPopoverClasses}>
                <h2 className={styles.popoverTitle}>
                    <Link href={props.basketURL} target="_top">
                        <strong className={styles.popoverTitleHighlight}>
                            {t`Your Cart`}
                        </strong>{" "}
                        <span>
                            ({totalQuantity} {qtyLabel})
                        </span>
                    </Link>
                </h2>
                <div className={`${styles.popoverLines} basket-popover__lines`}>
                    <BasketLines
                        financingPlans={financingPlans}
                        basket={basket}
                        getProductUpsell={getProductUpsell}
                        showValueProps={false}
                        predictedDeliveryDates={
                            PredictedDeliveryDateDisplayType.DISABLED
                        }
                        preferredDeliveryDates={
                            PreferredDeliveryDateDisplayType.DISABLED
                        }
                        loadConcreteBundles={(productURL) => {
                            dispatch(loadConcreteBundles({ productURL }));
                        }}
                        addBasketLine={(productURL, qty) => {
                            return dispatch(
                                addBasketLine({ productURL, qty }),
                            ).unwrap();
                        }}
                        updateBasketLineQuantity={(lineURL, newQty) => {
                            return dispatch(
                                updateBasketLineQuantity({ lineURL, newQty }),
                            ).unwrap();
                        }}
                        removeBasketLine={(lineURL) => {
                            return dispatch(
                                removeBasketLine({ lineURL }),
                            ).unwrap();
                        }}
                        variant={props.basketLineVariant}
                        isMobile={false}
                        removeVoucherCode={(code) => {
                            dispatch(removeVoucherCode({ code }));
                        }}
                    />
                </div>
                <div className="basket-popover__totals">
                    <div
                        className={`${styles.popoverTotal} basket-popover__total`}
                    >
                        <h3 className="basket-popover__total-column basket-popover__total-column--left">
                            {t`Subtotal`}
                        </h3>
                        <div className="basket-popover__total-column basket-popover__total-column--right">
                            <strong>{format.money(basketSubtotal)}</strong>
                        </div>
                    </div>
                    {props.buildPromoComponents &&
                        props.buildPromoComponents(onRequestClose)}
                    <Link
                        href={props.basketURL}
                        target="_top"
                        className={`${styles.popoverButton} button button--secondary`}
                    >
                        {t`View cart`}
                    </Link>
                    {!isBasketEmpty ? (
                        <Link
                            href={props.checkoutURL}
                            target="_top"
                            className={`${styles.popoverButton} button`}
                            id="basket-summary__checkout--cart-tracking"
                        >
                            {t`Checkout`}
                        </Link>
                    ) : (
                        <button
                            className={`${styles.popoverButton} button`}
                            disabled={true}
                        >
                            {t`Checkout`}
                        </button>
                    )}
                </div>
                {props.children && (
                    <ErrorBoundary>{props.children}</ErrorBoundary>
                )}
            </div>
        </UncontrolledPopover>
    );
};
